import React, { Suspense, lazy } from "react";
import { withChunkReloadGuard } from "../../lib/chunkReloadGuard";

/**
 * Code-split entry point for {@link SchedulePickerCalendar}.
 *
 * The picker is the only eagerly-reachable FullCalendar consumer — the calendar
 * view itself is already lazy — so importing it directly pulls the whole of
 * FullCalendar into the initial payload for a modal that opens on demand.
 * Import this module rather than `SchedulePickerCalendar` directly, or the
 * split is undone.
 */
const SchedulePickerCalendar = lazy(
  withChunkReloadGuard("schedulePickerChunkReloaded", () =>
    import("./SchedulePickerCalendar"),
  ),
);

type SchedulePickerCalendarProps = React.ComponentProps<
  typeof SchedulePickerCalendar
>;

/**
 * The picker fills a dialog panel that is already on screen, so the fallback
 * only has to hold that panel's height while the chunk lands.
 */
const PickerFallback: React.FC = () => (
  <div
    role="status"
    aria-label="Loading calendar"
    className="flex-1 flex items-center justify-center"
  >
    <div className="w-8 h-8 border-[3px] border-warm-border border-t-warm-blue rounded-full animate-spin" />
  </div>
);

const LazySchedulePickerCalendar: React.FC<SchedulePickerCalendarProps> = (
  props,
) => (
  <Suspense fallback={<PickerFallback />}>
    <SchedulePickerCalendar {...props} />
  </Suspense>
);

export default LazySchedulePickerCalendar;
